
import { useState, useCallback, useRef, useEffect } from "react";
import { useProjects } from "@/components/Auth/Projects/useProjects";
import { toast } from "sonner";
import { useAuth } from "@/context/auth";
import { useNavigate } from "react-router-dom";
import { parseRouteParams } from "@/utils/customRouter";

export const useConsolidatedProjectHandling = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { projects, sharedProjects, isLoading: projectsLoading, error, refreshProjects } = useProjects();
  const params = parseRouteParams();
  const routeProjectId = params.projektId;
  
  const [projectId, setProjectId] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string>("");
  const [hasProjects, setHasProjects] = useState<boolean | null>(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // Use ref to track if fetch is in progress to avoid duplicate calls
  const fetchInProgress = useRef(false);
  const lastFetchTime = useRef<number>(0);

  // Combined loading state - ensure we don't show content until everything is ready
  const isLoading = authLoading || projectsLoading;

  // Load project from URL only - don't auto-navigate to saved projects
  useEffect(() => {
    if (routeProjectId && !projectId) {
      console.log("Loading project from URL:", routeProjectId);
      setProjectId(routeProjectId);
      
      // Find project name
      const allProjects = [...projects, ...sharedProjects];
      const foundProject = allProjects.find(p => p.id === routeProjectId);
      if (foundProject) {
        setProjectName(foundProject.name);
        // Store in localStorage for future reference
        localStorage.setItem('selectedProjectId', routeProjectId);
      }
    } else if (!routeProjectId && projectId) {
      // Clear project state when no project ID in URL
      setProjectId(null);
      setProjectName("");
    }
  }, [routeProjectId, projectId, projects, sharedProjects]);

  // Consolidated effect to prevent multiple triggers
  useEffect(() => {
    if (!projectsLoading) {
      const total = projects.length + sharedProjects.length;
      console.log("Projects loaded, total count:", total);
      setHasProjects(total > 0);
      
      if (!initialLoadComplete) {
        console.log("Setting initial load complete");
        setInitialLoadComplete(true);
      }
    }
  }, [projectsLoading, projects.length, sharedProjects.length, initialLoadComplete]);

  // Set up project availability tracking - no longer auto-navigate
  const fetchDefaultProject = useCallback(async (): Promise<boolean> => {
    // Don't fetch if auth is loading or no user
    if (authLoading) {
      console.log("Auth loading, waiting for user...");
      return false;
    }

    if (!user) {
      console.log("No user, cannot fetch projects");
      setHasProjects(false);
      setProjectId(null);
      setProjectName("");
      setInitialLoadComplete(true);
      return false;
    }

    // If we already have a project from URL, don't override
    if (routeProjectId && projectId === routeProjectId) {
      setInitialLoadComplete(true);
      return true;
    }

    // Prevent duplicate calls within 2 seconds
    const now = Date.now();
    if (fetchInProgress.current || (now - lastFetchTime.current < 2000)) {
      console.log("Skipping duplicate fetchDefaultProject call");
      return false;
    }

    try {
      fetchInProgress.current = true;
      lastFetchTime.current = now;
      
      console.log("Checking projects for user:", user.id);
      
      // Wait for projects to be loaded if they're still loading
      if (projectsLoading) {
        console.log("Projects still loading, waiting...");
        return false;
      }

      const allProjects = [...projects, ...sharedProjects];
      console.log("Number of total projects found:", allProjects.length);

      if (allProjects.length > 0) {
        setHasProjects(true);
        setInitialLoadComplete(true);
        // DON'T auto-navigate - let user choose from overview page
        return true;
      }
      
      console.log("No projects found for user");
      setHasProjects(false);
      setProjectId(null);
      setProjectName("");
      setInitialLoadComplete(true);
      return false;
    } catch (error: any) {
      console.error("Error checking projects:", error);
      setHasProjects(false);
      setInitialLoadComplete(true);
      return false;
    } finally {
      fetchInProgress.current = false;
    }
  }, [user, authLoading, projects, sharedProjects, projectsLoading, routeProjectId, projectId]);

  // Handle project selection with improved logging and URL update
  const handleSelectProject = useCallback(async (newProjectId: string) => {
    if (!user) {
      toast.error("Du måste vara inloggad för att välja projekt");
      return newProjectId;
    }

    console.log("Selecting project:", newProjectId);
    setProjectId(newProjectId);
    setHasProjects(true);
    
    // Find the project name from our projects lists
    const allProjects = [...projects, ...sharedProjects];
    const selectedProject = allProjects.find(p => p.id === newProjectId);
    const name = selectedProject?.name || "";
    
    console.log("Project name found:", name);
    setProjectName(name);
    
    // Store in localStorage and update URL
    localStorage.setItem('selectedProjectId', newProjectId);
    navigate(`/uppdrag/${newProjectId}`, { replace: true });
    
    // Toast with more info
    toast.success(`Projekt "${name}" valt`, {
      description: `Projekt-ID: ${newProjectId}`
    });
    
    return newProjectId;
  }, [projects, sharedProjects, user]);

  // Reset state when needed
  const resetState = useCallback(() => {
    setProjectId(null);
    setProjectName("");
    setHasProjects(null);
    setInitialLoadComplete(false);
    localStorage.removeItem('selectedProjectId');
    fetchInProgress.current = false;
    lastFetchTime.current = 0;
  }, []);

  return {
    projectId,
    projectName,
    fetchError: error,
    hasProjects,
    isLoading,
    initialLoadComplete,
    setProjectId,
    setProjectName,
    fetchDefaultProject,
    handleSelectProject,
    resetState,
    // Expose the underlying projects data for other uses
    projects,
    sharedProjects,
    refreshProjects
  };
};
